<template>
  <v-row justify="end" no-gutters class="fill-height">
    <v-col class="d-none d-md-block col-xl-8 col-lg-7 col-md-6 col-12">
      <div class="login-image"></div>
    </v-col>
    <v-col class="white d-flex align-center col-xl-4 col-lg-5 col-md-6 col-12 ">
      <v-row justify="center">
        <v-col
          class="col col-sm-8 col-md-8 col-lg-9 px-12 px-sm-10 px-md-4 px-lg-8"
        >
          <div class="text-center">
            <SvgLogo class="mx-auto mb-8" width="390" iconType="StrixLogo" />
          </div>

          <v-form ref="form" @submit.prevent v-model="valid" lazy-validation>
            <div class="mb-3">
              <span class="mb-2 input-title text-body-2 inline-block">{{
                $t('portal.login.login.labels.label_email_input')
              }}</span>
              <v-text-field
                v-model="email"
                class="rounded-0"
                dense
                height="48"
                color="primary"
                filled
                @keydown.space.prevent
                hide-details="auto"
                :rules="emailRules"
                required
                @keyup.enter="submit"
                id="idLoginUsuario"
              >
              </v-text-field>
            </div>

            <div class="mb-2">
              <span class="mb-2 input-title text-body-2 inline-block">{{
                $t('portal.login.login.labels.label_password_input')
              }}</span>
              <v-text-field
                v-model="password"
                class="rounded-0"
                dense
                height="48"
                color="primary"
                filled
                hide-details="auto"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :rules="[rules.required, rules.min]"
                :type="showPassword ? 'text' : 'password'"
                @keyup.enter="submit"
                @keypress="detectCapsLock"
                @click:append="togglePassword"
                id="idLoginContrasena"
              >
              </v-text-field>
            </div>

            <p class="text-right">
              <a
                class="deepSkyBlue--text text-decoration-none text-body-2"
                @click="routerLink"
                id="idLoginOlvidoContrasena"
                >{{ $t('portal.login.login.labels.forgot_password') }}</a
              >
            </p>

            <v-alert
              dismissible
              v-model="alert"
              close-icon="mdi-close"
              dark
              transition="fade-transition"
              color="primary"
            >
              <div class="white--text font-weight-bold mb-0">
                {{ $t('portal.login.login.alerts.title') }}
              </div>
              <div class="white--text mb-0">{{ alertMessage }}</div>
            </v-alert>

            <v-btn
              class="mt-7 text-uppercase text-caption font-weight-bold"
              dark
              color="deepSkyBlue"
              block
              depressed
              rounded
              height="48"
              :loading="loading"
              @click="submit"
              id="idBtnLoginIngresar"
              >{{ $t('portal.login.login.labels.submit_button') }}</v-btn
            >
          </v-form>
          <p
            class="text-center mt-1"
            v-if="$t('portal.login.login.labels.label_account')"
          >
            <router-link
              class="primary--text text-decoration-none text-body-2"
              :to="{ name: 'register' }"
              >{{ $t('portal.login.login.labels.label_account') }}</router-link
            >
          </p>
        </v-col>
      </v-row>
      <DialogTermsAndConditions
        v-if="noVersionAccepted"
        :noVersionAccepted="noVersionAccepted"
        :htmlText="htmlText"
        @cancelTermsConditionsModal="cancelTermsConditionsModal"
        @versionAcceptTermsConditions="acceptNewVersionTermsConditions"
      />
    </v-col>
  </v-row>
</template>

<script>
import DialogTermsAndConditions from './DialogTermsAndConditions'
import { SvgLogo, firebaseServices } from '@leapsight/styleguide'
import { mapGetters, mapActions } from 'vuex'
import firebaseTagNames from '@/constants/firebaseTagNames'
import { DeviceHelpers } from '@/helpers/mainExports'
import i18n from '@/plugins/i18n'

export default {
  name: 'Login',
  components: {
    SvgLogo,
    DialogTermsAndConditions
  },
  data: () => ({
    loading: false,
    noVersionAccepted: false,
    htmlText: '',
    valid: false,
    showPassword: false,
    alert: false,
    alertMessage: '',
    password: '',
    email: '',
    emailRules: [
      v => !!v || i18n.t('portal.login.login.rules.email_required'),
      v =>
        /.+@.+\..+/.test(v) || i18n.t('portal.login.login.rules.email_format')
    ],
    rules: {
      required: value =>
        !!value || i18n.t('portal.login.login.rules.password_required'),
      min: v =>
        v.length >= 8 || i18n.t('portal.login.login.rules.password_length')
    }
  }),
  mounted() {
    this.setIconId()
  },
  computed: {
    ...mapGetters('auth', ['userIsAdmin', 'accountData']),
    userIsAdmin() {
      return this.userIsAdmin
    }
  },
  watch: {
    showPassword() {
      this.$nextTick(() => {
        this.setIconId()
      })
    }
  },
  methods: {
    ...mapActions('auth', [
      'login',
      'logout',
      'establishConnection',
      'checkVersionTermsConditions',
      'versionAcceptTermsConditions',
      'accountCheckVersion',
      'getLastVersionTermsCondition'
    ]),
    togglePassword() {
      this.showPassword = !this.showPassword
      this.$nextTick(() => {
        this.setIconId()
      })
    },
    setIconId() {
      const eyeIcon = this.$el.querySelector('.v-icon')
      if (eyeIcon) {
        eyeIcon.setAttribute('id', 'idLoginEye')
      }
    },
    routerLink() {
      this.logEventFireBase()
      this.$router.push({ name: 'forgot-password' })
    },
    logEventFireBase() {
      firebaseServices.logEvent(
        firebaseServices.analytics,
        firebaseTagNames.BTN_PORTAL_FORGOT_PASSWORD
      )
    },
    detectCapsLock(e) {
      const IS_MAC = /Mac/.test(navigator.platform)
      let capsLock = false
      const charCode = e.charCode
      const shiftKey = e.shiftKey
      // a-z minuscula
      if (charCode >= 97 && charCode <= 122) {
        capsLock = shiftKey
      }
      // A - Z mayuscula sin que se presione shift y no sea plataforma MAC
      else if (charCode >= 65 && charCode <= 90 && !(shiftKey && IS_MAC)) {
        capsLock = !shiftKey
        this.alertMessage = i18n.t('portal.login.login.alerts.label_capslock')
      }
      this.alert = capsLock
    },
    injectTermsConditionsHtml(data) {
      this.htmlText = data
    },

    async deviceCreate() {
      try {
        await DeviceHelpers.create()
      } catch (e) {
        console.error(e)
      }
    },

    submit() {
      //Valido formulario
      if (this.$refs.form.validate()) {
        firebaseServices.logEvent(
          firebaseServices.analytics,
          firebaseTagNames.BTN_PORTAL_LOGIN
        )
        const email = this.email.trim()
        const password = this.password
        this.loading = !this.loading
        //Valida User y Pass contra bondy Oauth
        this.login({ email, password })
          // Proceso luego de login exitoso
          .then(() => {
            //Establece conexión wamp y verifica si el usuario es Admin
            this.establishConnection()
              .then(userIsAdmin => {
                // Registro del device en la plataforma
                // this.deviceCreate()
                // El usuario debe poseer una cuenta corporativa
                if (this.userHasCorporateAccount()) {
                  if (userIsAdmin) {
                    //De ser Admin Verifica la ultima version termsCond y las aceptadas
                    this.accountCheckVersion()
                      .then(async accountCheckVersion => {
                        await this.getLastVersionTermsCondition()
                        // Si tiene alguna version aceptada entonces ingresa
                        if (accountCheckVersion.length > 0) {
                          this.$router.push({ name: 'launcher' })
                          // De lo contrario, se muestra el modal de TyC
                        } else {
                          this.noVersionAccepted = true
                          this.checkVersionTermsConditions()
                            .then(res => {
                              this.injectTermsConditionsHtml(res)
                            })
                            .catch(err => {
                              //Error en la wampcall de términos
                              console.error(err)
                              this.errorProcess(
                                'portal.login.login.errors.terms_conditions_service_unreachable'
                              )
                            })
                        }
                      })
                      .catch(err => {
                        console.error(err)
                        this.errorProcess()
                      })
                  } else {
                    //Usuario no administrador ingresa a la plataforma
                    this.$router.push({ name: 'launcher' })
                  }
                } else {
                  //Proceso para usuario sin cuenta corporativa
                  this.errorProcess('portal.login.login.errors.not_fleet_user')
                }
              })
              // Catch de establishConnection
              .catch(err => {
                console.error(err)
                this.errorProcess('portal.login.login.errors.others')
              })
          })
          // Proceso luego de login fallido
          .catch(err => {
            console.error(err)
            this.loading = !this.loading
            this.alert = true
            if (
              err.hasOwnProperty('data') &&
              err.data.code == 'invalid_grant'
            ) {
              this.alertMessage = i18n.t(
                'portal.login.login.errors.credentials'
              )
            } else if (err) {
              this.alertMessage = i18n.t('portal.login.login.errors.others')
            }
          })
      }
    },
    acceptNewVersionTermsConditions() {
      this.versionAcceptTermsConditions()
        .then(() => {
          this.$store.commit('auth/termsConditionsBlocker', true)
          this.$router.push({ name: 'launcher' })
        })
        .catch(err => {
          console.log(err)
        })
    },
    cancelTermsConditionsModal() {
      this.noVersionAccepted = !this.noVersionAccepted
      this.errorProcess('portal.login.login.errors.cancel_terms_conditions')
    },
    userHasCorporateAccount() {
      const userMeta = this.accountData.meta
      return (
        userMeta.hasOwnProperty('business_account_id') &&
        userMeta.hasOwnProperty('business_user_id')
      )
    },
    errorProcess(errMsge = null) {
      localStorage.clear()
      this.logout()
      this.loading = !this.loading
      this.alert = true
      if (errMsge !== null) this.alertMessage = i18n.t(errMsge)
      else this.alertMessage = i18n.t('portal.login.login.errors.others')
    }
  }
}
</script>
<style scoped>
.input-title {
  color: #007cfd;
  font-size: 0.8rem;
}
.login-image {
  background-image: url('../../assets/bg-login.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;
  background-position: 0% 0%;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
}
.orange--text {
  color: var(--orange) !important;
}
</style>
